import React, { useContext, useEffect, useMemo, useState } from "react";
import { Badge, OverlayTrigger, Tooltip, Popover } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddUserSidebar from "./AddUserSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import ConfirmModal from "../../components/modals/ConfirmModal";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Users",
    SEARCH_PLACEHOLDER: "Search",
    DELETE_TOOLTIP_HINT: "Please select local users only",
};

// Confirmation messages
const USER_MESSAGES = {
    DELETE_USERS_BULK: (count) => `You are about to delete ${count} user(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const GET_USERS = gql`
    query Users {
        users {
            id
            email
            name
            username
            is_active
            description
            home_timezone
            current_timezone
            show_time_in_home_timezone
            identities {
                provider
            }
            memberships {
                plant {
                    id
                    name
                }
                role {
                    id
                    name
                }
            }
        }
    }
`;

const DELETE_ONE_USER = gql`
    mutation deleteOneUser($id: Int!) {
        deleteOneUser(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_USER = gql`
    mutation deleteManyUser($ids: [Int!]!) {
        deleteManyUser(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const formatIdentityProviders = (row) =>
    (row?.identities || []).map((i) => i?.provider).filter(Boolean);

const formatMemberships = (row) =>
    (row?.memberships || [])
        .map((m) => {
            const plant = m?.plant?.name || "-";
            const role = m?.role?.name || "-";
            return `${plant}: ${role}`;
        })
        .filter(Boolean);

const isOnlyLocalIdentity = (row) => {
    const providers = formatIdentityProviders(row);
    return (
        providers.length > 0 &&
        providers.every((p) => p === "local-identity-provider")
    );
};

const Users = () => {
    const starekContext = useContext(StarekContext);

    const { data, loading, error, refetch } = useQuery(GET_USERS, {
        fetchPolicy: "network-only",
    });

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const [deleteOneUser] = useMutation(DELETE_ONE_USER, {
        client: starekContext.engineClient,
    });
    const [deleteManyUser] = useMutation(DELETE_MANY_USER, {
        client: starekContext.engineClient,
    });

    const localOnlySelection =
        selectedRows.length > 0 && selectedRows.every(isOnlyLocalIdentity);
    const canDelete = selectedRows.length > 0 && localOnlySelection;
    const anyNonLocalSelected =
        selectedRows.length > 0 &&
        selectedRows.some((r) => !isOnlyLocalIdentity(r));
    const showDeleteTooltip = anyNonLocalSelected;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error]);

    useEffect(() => {
        if (!loading && data?.users) {
            setItems(data.users);
        }
    }, [data, loading]);

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter((u) => {
            const membershipsText = formatMemberships(u)
                .join(" ")
                .toLowerCase();
            const plantNames = (u?.memberships || [])
                .map((m) => m?.plant?.name || "")
                .join(" ")
                .toLowerCase();
            return (
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                membershipsText.includes(q) ||
                plantNames.includes(q) ||
                (u.description || "").toLowerCase().includes(q)
            );
        });
    }, [items, filterText]);

    const handleAdd = () => {
        setShowWizard(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowWizard(true);
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        await refetch();
        setToggleCleared((p) => !p);
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            const ids = selectedRows.map((u) => Number(u.id));
            const total = ids.length;
            if (ids.length === 1) {
                await deleteOneUser({ variables: { id: ids[0] } });
                setDeleteDone(1);
            } else {
                await deleteManyUser({ variables: { ids } });
                setDeleteDone(total);
            }
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
            try {
                starekContext?.showSuccess?.("User(s) deleted");
            } catch {}
        } catch (error) {
            starekContext.showError(error);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
            setConfirmCfg({ open: false });
        }
    };

    const NameCell = ({ row }) => (
        <span role="button" className="text-primary">
            {row.name || "-"}
        </span>
    );

    const MembershipsCell = ({ row }) => {
        const memberships = formatMemberships(row);
        const firstTwo = memberships.slice(0, 2);
        const extra = memberships.length - firstTwo.length;

        const pop = (
            <Popover id={`membership-pop-${row.id}`}>
                <Popover.Body className="p-2">
                    {memberships.length === 0 ? (
                        <div className="text-muted">No memberships</div>
                    ) : (
                        memberships.map((m, i) => (
                            <div key={i} className="py-1">
                                {m}
                            </div>
                        ))
                    )}
                </Popover.Body>
            </Popover>
        );

        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {firstTwo.map((m) => (
                    <Badge key={m} bg="light" text="dark" className="border">
                        {m}
                    </Badge>
                ))}
                {extra > 0 && (
                    <OverlayTrigger
                        trigger="click"
                        placement="right"
                        overlay={pop}
                        rootClose
                    >
                        <Badge
                            bg="light"
                            text="dark"
                            className="border"
                            role="button"
                        >
                            +{extra} more
                        </Badge>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    const IdentityProvidersCell = ({ row }) => {
        const providers = formatIdentityProviders(row);
        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {providers.map((p) => (
                    <Badge key={p} bg="light" text="dark" className="border">
                        {p}
                    </Badge>
                ))}
            </div>
        );
    };

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            cell: (row) => <NameCell row={row} />,
            grow: 1.1,
        },
        {
            name: "Email",
            selector: (r) => r.email || "-",
            sortable: true,
            grow: 1.2,
        },
        {
            name: "Memberships",
            selector: (r) => formatMemberships(r).join(", "),
            sortable: false,
            cell: (row) => <MembershipsCell row={row} />,
            grow: 1.6,
        },
        {
            name: "Identity Provider",
            selector: (r) => formatIdentityProviders(r).join(", "),
            sortable: false,
            cell: (row) => <IdentityProvidersCell row={row} />,
            grow: 1.2,
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 420 }}>
                    {row.description || "-"}
                </div>
            ),
            grow: 2,
        },
    ];

    if (loading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-user fa-10x text-success"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "User", plural: "Users" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                deleteDisabled={!canDelete}
                deleteTooltip={
                    showDeleteTooltip ? UI_STRINGS.DELETE_TOOLTIP_HINT : null
                }
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleBulkDelete}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        striped
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        responsive
                        pagination
                        dense
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <AddUserSidebar
                show={showWizard}
                mode={selectedRows[0]?.id ? "edit" : "add"}
                initialValues={selectedRows[0] || {}}
                onClose={handleDrawerClose}
                onFinish={handleDrawerClose}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Users };
